import { type SyntheticEvent, useEffect, useMemo, useState } from 'react';
import AppBar from '@mui/material/AppBar';
import Card from '@mui/material/Card';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import { useNavigate } from 'react-router-dom';

import SoftBadge from 'components/SoftBadge';
import SoftButton from 'components/SoftButton';
import SoftBox from 'components/SoftBox';
import SoftTypography from 'components/SoftTypography';
import { showSuiErrorSnackbar } from 'components/SuiSnackbar';

import breakpoints from 'assets/theme/base/breakpoints';
import { useAuth } from 'auth';
import CustomerSupport from 'examples/Icons/CustomerSupport';
import DocumentIcon from 'examples/Icons/Document';
import DashboardLayout from 'examples/LayoutContainers/DashboardLayout';
import Footer from 'examples/Footer';
import DashboardNavbar from 'examples/Navbars/DashboardNavbar';
import SpaceShip from 'examples/Icons/SpaceShip';
import { useLocale } from 'lib/i18n/client';
import { getTranslations, interpolateTemplate } from 'lib/i18n/dictionary';
import {
  confirmProposalRequest,
  declineProposalRequest,
  fetchProfileProposals,
  readCachedProfileProposals,
  type ProposalStatus,
  type ProposalSummary,
} from 'proposal';

type ProposalSectionKey = 'active' | 'archived';
type ProposalFilterKey = 'invites' | 'discussing' | 'submitted';

function getProposalSection(status: ProposalStatus): ProposalSectionKey {
  return status === 'SUBMITTED' ||
    status === 'VIEWED' ||
    status === 'DISCUSSING' ||
    status === 'APPROVED'
    ? 'active'
    : 'archived';
}

function formatProposalDate(value: string): string {
  const timestamp = Date.parse(value);
  if (!Number.isFinite(timestamp)) {
    return value;
  }

  try {
    return new Intl.DateTimeFormat(undefined, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    }).format(new Date(timestamp));
  } catch {
    return new Date(timestamp).toISOString();
  }
}

function formatProposalPrice(value: number, currency: string): string {
  if (!Number.isFinite(value)) {
    return `${currency} 0.00`;
  }

  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
  } catch {
    return `${currency} ${value.toFixed(2)}`;
  }
}

function truncateText(value: string, maxLength: number): string {
  if (value.length <= maxLength) {
    return value;
  }

  return `${value.slice(0, maxLength - 3).trimEnd()}...`;
}

function ProposalFilterSidenav({
  items,
  selectedFilter,
  onSelectFilter,
}: {
  items: Array<{ key: ProposalFilterKey; label: string; icon: any }>;
  selectedFilter: ProposalFilterKey;
  onSelectFilter: (filter: ProposalFilterKey) => void;
}) {
  return (
    <Card
      sx={{
        borderRadius: ({ borders: { borderRadius } }: any) => borderRadius.lg,
        position: 'sticky',
        top: '1%',
      }}
    >
      <SoftBox
        component="ul"
        display="flex"
        flexDirection="column"
        p={2}
        m={0}
        sx={{ listStyle: 'none' }}
      >
        {items.map(({ key, label, icon }, index) => {
          const isSelected = selectedFilter === key;

          return (
            <SoftBox key={key} component="li" pt={index === 0 ? 0 : 1}>
              <SoftBox
                component="button"
                type="button"
                onClick={() => onSelectFilter(key)}
                sx={({
                  borders: { borderRadius },
                  functions: { pxToRem },
                  palette: { light },
                  transitions,
                }: any) => ({
                  width: '100%',
                  border: 0,
                  display: 'flex',
                  alignItems: 'center',
                  textAlign: 'left',
                  font: 'inherit',
                  appearance: 'none',
                  WebkitAppearance: 'none',
                  background: 'none',
                  backgroundColor: isSelected ? light.main : 'transparent',
                  borderRadius: borderRadius.md,
                  padding: `${pxToRem(10)} ${pxToRem(16)}`,
                  transition: transitions.create('background-color', {
                    easing: transitions.easing.easeInOut,
                    duration: transitions.duration.shorter,
                  }),
                  cursor: 'pointer',

                  '&:hover': {
                    backgroundColor: light.main,
                  },

                  '& svg': {
                    marginBottom: '0 !important',
                    marginRight: 0,
                  },
                })}
              >
                <SoftBox display="flex" alignItems="center">
                  <SoftBox mr={1.5} lineHeight={1}>
                    {icon}
                  </SoftBox>
                  <SoftTypography
                    variant="button"
                    fontWeight="regular"
                    color="text"
                    textTransform="none"
                  >
                    {label}
                  </SoftTypography>
                </SoftBox>
              </SoftBox>
            </SoftBox>
          );
        })}
      </SoftBox>
    </Card>
  );
}

function Proposals() {
  const navigate = useNavigate();
  const { account, accessToken, activeProfileUuid, isProfilesLoading } = useAuth();
  const locale = useLocale();
  const translations = getTranslations(locale);
  const cachedOffers = activeProfileUuid
    ? readCachedProfileProposals(activeProfileUuid, false)
    : undefined;
  const [selectedSection, setSelectedSection] = useState<ProposalSectionKey>('active');
  const [selectedFilter, setSelectedFilter] = useState<ProposalFilterKey>('invites');
  const [offers, setOffers] = useState<ProposalSummary[]>(() =>
    (cachedOffers ?? []).filter((offer) => offer.status !== 'CONFIRMED')
  );
  const [isLoading, setIsLoading] = useState(() => cachedOffers === undefined);
  const [isMutating, setIsMutating] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [tabsOrientation, setTabsOrientation] = useState<'horizontal' | 'vertical'>('horizontal');

  const selectedSectionTabIndex = selectedSection === 'active' ? 0 : 1;

  useEffect(() => {
    function handleTabsOrientation() {
      setTabsOrientation(
        window.innerWidth < breakpoints.values.sm ? 'vertical' : 'horizontal'
      );
    }

    window.addEventListener('resize', handleTabsOrientation);
    handleTabsOrientation();

    return () => window.removeEventListener('resize', handleTabsOrientation);
  }, []);

  useEffect(() => {
    if (errorMessage) {
      showSuiErrorSnackbar(errorMessage);
    }
  }, [errorMessage]);

  const loadOffers = async (profileUuid: string, section: ProposalSectionKey) => {
    const nextOffers = await fetchProfileProposals(
      profileUuid,
      section === 'archived',
      accessToken ?? undefined
    );
    setOffers(nextOffers.filter((offer) => offer.status !== 'CONFIRMED'));
  };

  useEffect(() => {
    if (account?.uuid && isProfilesLoading) {
      setIsLoading(true);
      return;
    }

    let isCancelled = false;

    async function reloadOffers() {
      const cachedData = activeProfileUuid
        ? readCachedProfileProposals(activeProfileUuid, selectedSection === 'archived')
        : undefined;

      if (!isCancelled && cachedData !== undefined) {
        setOffers(cachedData.filter((offer) => offer.status !== 'CONFIRMED'));
        setIsLoading(false);
      } else if (!isCancelled) {
        setIsLoading(true);
      }

      setErrorMessage('');

      if (!activeProfileUuid) {
        if (!isCancelled) {
          setOffers([]);
          setIsLoading(false);
        }
        return;
      }

      try {
        const nextOffers = await fetchProfileProposals(
          activeProfileUuid,
          selectedSection === 'archived',
          accessToken ?? undefined
        );

        if (!isCancelled) {
          setOffers(nextOffers.filter((offer) => offer.status !== 'CONFIRMED'));
        }
      } catch (error) {
        if (!isCancelled) {
          setErrorMessage(
            error instanceof Error ? error.message : translations.proposalsPage.loadFailed
          );
          setOffers([]);
        }
      } finally {
        if (!isCancelled) {
          setIsLoading(false);
        }
      }
    }

    void reloadOffers();

    return () => {
      isCancelled = true;
    };
  }, [
    accessToken,
    account?.uuid,
    activeProfileUuid,
    isProfilesLoading,
    selectedSection,
    translations.proposalsPage.loadFailed,
  ]);

  const activeOffers = useMemo(
    () => offers.filter((offer) => getProposalSection(offer.status) === 'active'),
    [offers]
  );

  const filterItems = useMemo(
    () => [
      {
        key: 'invites' as const,
        icon: <SpaceShip size="16px" />,
        label: interpolateTemplate(translations.proposalsPage.invitesFilterLabel, {
          count: String(activeOffers.filter((offer) => offer.status === 'APPROVED').length),
        }),
      },
      {
        key: 'discussing' as const,
        icon: <CustomerSupport size="16px" />,
        label: interpolateTemplate(translations.proposalsPage.discussingFilterLabel, {
          count: String(activeOffers.filter((offer) => offer.status === 'DISCUSSING').length),
        }),
      },
      {
        key: 'submitted' as const,
        icon: <DocumentIcon size="16px" />,
        label: interpolateTemplate(translations.proposalsPage.submittedFilterLabel, {
          count: String(
            activeOffers.filter(
              (offer) => offer.status === 'SUBMITTED' || offer.status === 'VIEWED'
            ).length
          ),
        }),
      },
    ],
    [activeOffers, translations.proposalsPage]
  );

  const visibleOffers = useMemo(() => {
    return offers.filter((offer) => {
      if (getProposalSection(offer.status) !== 'active') {
        return selectedSection === 'archived';
      }

      if (selectedSection === 'archived') {
        return false;
      }

      if (selectedFilter === 'invites') {
        return offer.status === 'APPROVED';
      }

      if (selectedFilter === 'discussing') {
        return offer.status === 'DISCUSSING';
      }

      return offer.status === 'SUBMITTED' || offer.status === 'VIEWED';
    });
  }, [offers, selectedFilter, selectedSection]);

  const handleOpenPosition = (positionUuid: string) => {
    navigate(`/positions/${positionUuid}`);
  };

  const handleSectionTabChange = (_event: SyntheticEvent, nextValue: number) => {
    setSelectedSection(nextValue === 0 ? 'active' : 'archived');
  };

  const handleAcceptProposal = async (offerUuid: string) => {
    if (!activeProfileUuid || !account?.uuid) {
      return;
    }

    setIsMutating(true);
    setErrorMessage('');

    try {
      await confirmProposalRequest(offerUuid, account.uuid, accessToken ?? undefined);
      await loadOffers(activeProfileUuid, selectedSection);
    } catch (error) {
      setErrorMessage(
        error instanceof Error ? error.message : translations.proposalsPage.acceptFailed
      );
    } finally {
      setIsMutating(false);
    }
  };

  const handleDeclineProposal = async (offerUuid: string) => {
    if (!activeProfileUuid || !account?.uuid) {
      return;
    }

    setIsMutating(true);
    setErrorMessage('');

    try {
      await declineProposalRequest(offerUuid, account.uuid, accessToken ?? undefined);
      await loadOffers(activeProfileUuid, selectedSection);
    } catch (error) {
      setErrorMessage(
        error instanceof Error ? error.message : translations.proposalsPage.declineFailed
      );
    } finally {
      setIsMutating(false);
    }
  };

  const getProposalStatusLabel = (status: ProposalStatus): string => {
    switch (status) {
      case 'APPROVED':
        return translations.proposalsPage.statusApproved;
      case 'CANCELED':
        return translations.proposalsPage.statusCanceled;
      case 'DECLINED':
        return translations.proposalsPage.statusDeclined;
      case 'DISCUSSING':
        return translations.proposalsPage.statusDiscussing;
      case 'REJECTED':
        return translations.proposalsPage.statusRejected;
      case 'SUBMITTED':
        return translations.proposalsPage.statusSubmitted;
      case 'VIEWED':
        return translations.proposalsPage.statusViewed;
      default:
        return status;
    }
  };

  const getProposalStatusColor = (status: ProposalStatus) => {
    switch (status) {
      case 'APPROVED':
        return 'success';
      case 'DISCUSSING':
        return 'info';
      case 'SUBMITTED':
      case 'VIEWED':
        return 'dark';
      case 'DECLINED':
        return 'warning';
      case 'REJECTED':
      case 'CANCELED':
        return 'error';
      default:
        return 'secondary';
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <SoftBox pb={3}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Card>
              <SoftBox p={3}>
                <Stack spacing={2}>
                  <SoftBox lineHeight={1}>
                    <SoftTypography variant="h5" fontWeight="medium">
                      {translations.proposalsPage.title}
                    </SoftTypography>
                    <SoftTypography variant="button" fontWeight="regular" color="text">
                      {translations.proposalsPage.listDescription}
                    </SoftTypography>
                  </SoftBox>

                  {!account?.uuid ? (
                    <SoftTypography variant="button" color="text">
                      {translations.proposalsPage.accountMissing}
                    </SoftTypography>
                  ) : !isProfilesLoading && !activeProfileUuid ? (
                    <SoftTypography variant="button" color="text">
                      {translations.proposalsPage.profileMissing}
                    </SoftTypography>
                  ) : null}
                </Stack>
              </SoftBox>
            </Card>
          </Grid>

          <Grid item xs={12} sm={8} lg={4}>
            <AppBar position="static">
              <Tabs
                orientation={tabsOrientation}
                value={selectedSectionTabIndex}
                onChange={handleSectionTabChange}
                sx={{
                  '&.MuiTabs-root': {
                    backgroundColor: 'transparent !important',
                  },
                }}
              >
                <Tab label={translations.proposalsPage.activeLabel} />
                <Tab label={translations.proposalsPage.archivedLabel} />
              </Tabs>
            </AppBar>
          </Grid>

          <Grid item xs={12}>
            <Grid container spacing={3}>
              {selectedSection === 'active' ? (
                <Grid item xs={12} lg={3}>
                  <ProposalFilterSidenav
                    items={filterItems}
                    selectedFilter={selectedFilter}
                    onSelectFilter={setSelectedFilter}
                  />
                </Grid>
              ) : null}

              <Grid item xs={12} lg={selectedSection === 'active' ? 9 : 12}>
                {isLoading ? (
                  <SoftTypography variant="button" color="text">
                    {translations.proposalsPage.loading}
                  </SoftTypography>
                ) : visibleOffers.length === 0 ? (
                  <Card>
                    <SoftBox p={4} textAlign="center">
                      <SoftTypography variant="button" color="text">
                        {translations.proposalsPage.empty}
                      </SoftTypography>
                    </SoftBox>
                  </Card>
                ) : (
                  <Grid container spacing={3}>
                    {visibleOffers.map((offer) => {
                      const normalizedServiceTitle = offer.service?.title?.trim();
                      const subtitle = normalizedServiceTitle
                        ? interpolateTemplate(translations.proposalsPage.serviceSubtitle, {
                            serviceTitle: normalizedServiceTitle,
                          })
                        : getProposalStatusLabel(offer.status);

                      return (
                        <Grid item xs={12} md={selectedSection === 'active' ? 12 : 6} key={offer.uuid}>
                          <Card
                            sx={{
                              height: '100%',
                              cursor: 'pointer',
                              transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                              '&:hover': {
                                transform: 'translateY(-2px)',
                                boxShadow: (theme) => theme.shadows[8],
                              },
                            }}
                            onClick={() => handleOpenPosition(offer.position.uuid)}
                          >
                            <SoftBox p={3}>
                              <Stack spacing={2.5}>
                                <SoftBox
                                  display="flex"
                                  justifyContent="space-between"
                                  alignItems="flex-start"
                                  gap={2}
                                >
                                  <SoftBox pr={1}>
                                    <SoftTypography variant="h5" fontWeight="medium">
                                      {offer.position.title}
                                    </SoftTypography>
                                    <SoftTypography variant="button" color="text" fontWeight="regular">
                                      {subtitle}
                                    </SoftTypography>
                                  </SoftBox>
                                  <SoftTypography variant="caption" color="text" textAlign="right">
                                    {formatProposalDate(offer.createdAt)}
                                  </SoftTypography>
                                </SoftBox>

                                <SoftBox
                                  display="flex"
                                  justifyContent="space-between"
                                  alignItems={{ xs: 'flex-start', sm: 'center' }}
                                  flexDirection={{ xs: 'column', sm: 'row' }}
                                  gap={1.5}
                                >
                                  <SoftBadge
                                    variant="gradient"
                                    color={getProposalStatusColor(offer.status)}
                                    size="sm"
                                    badgeContent={getProposalStatusLabel(offer.status)}
                                    container
                                  />
                                  <SoftTypography variant="button" fontWeight="medium">
                                    {translations.proposalsPage.proposedPriceLabel}:{' '}
                                    {formatProposalPrice(offer.price, offer.currency)}
                                  </SoftTypography>
                                </SoftBox>

                                {offer.message?.trim() ? (
                                  <SoftTypography variant="button" color="text" fontWeight="regular">
                                    {truncateText(offer.message.trim(), 160)}
                                  </SoftTypography>
                                ) : null}

                                <Stack
                                  direction={{ xs: 'column', sm: 'row' }}
                                  spacing={1.5}
                                  justifyContent="space-between"
                                >
                                  <SoftButton
                                    variant="outlined"
                                    color="dark"
                                    size="small"
                                    onClick={(event) => {
                                      event.stopPropagation();
                                      handleOpenPosition(offer.position.uuid);
                                    }}
                                  >
                                    {translations.proposalsPage.openPositionAction}
                                  </SoftButton>

                                  {offer.status === 'APPROVED' ? (
                                    <Stack direction="row" spacing={1.5}>
                                      <SoftButton
                                        variant="gradient"
                                        color="success"
                                        size="small"
                                        disabled={isMutating}
                                        onClick={(event) => {
                                          event.stopPropagation();
                                          void handleAcceptProposal(offer.uuid);
                                        }}
                                      >
                                        {translations.proposalsPage.acceptAction}
                                      </SoftButton>
                                      <SoftButton
                                        variant="outlined"
                                        color="error"
                                        size="small"
                                        disabled={isMutating}
                                        onClick={(event) => {
                                          event.stopPropagation();
                                          void handleDeclineProposal(offer.uuid);
                                        }}
                                      >
                                        {translations.proposalsPage.declineAction}
                                      </SoftButton>
                                    </Stack>
                                  ) : null}
                                </Stack>
                              </Stack>
                            </SoftBox>
                          </Card>
                        </Grid>
                      );
                    })}
                  </Grid>
                )}
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </SoftBox>
      <Footer />
    </DashboardLayout>
  );
}

export default Proposals;
